/**
 * Locale handling for city insights.
 *
 * A stored city-insights row keeps its numeric data and proper nouns once (in
 * the base, English payload) and holds only the localizable *text* for each
 * additional locale under `translations`. `selectLocalizedCityInsights` overlays
 * the requested locale's text on top of the shared base so the renderer always
 * receives a plain `CityInsightsData`.
 *
 * Keep the localizable field set (`overview` + `aiOverview`) in sync with the
 * translation prompt in `build-prompt.ts` and the validator in `validate.ts`.
 */

import { CountryCodeList, type CountryCode } from 'types/countries';

import type { CityInsightsData } from 'components/home-page/local-focus/city-insights';

/** Base locale — the shape stored at the top level of every row. */
export const BASE_INSIGHTS_LANG = 'en' as const;

/** Locales we generate translations for. Base ('en') is always present. */
export type InsightsLang = 'en' | 'fr';

/** The subset of `CityInsightsData` that is translated per locale. */
export type LocalizedInsightsText = Pick<CityInsightsData, 'overview' | 'aiOverview'>;

/**
 * The row as persisted in storage: the base (English) `CityInsightsData` plus
 * an optional map of per-locale text overrides. Rows created before locale
 * support simply lack `translations`.
 */
export interface StoredCityInsights extends CityInsightsData {
  translations?: Partial<Record<InsightsLang, LocalizedInsightsText>>;
}

/**
 * Normalize a Next.js locale (e.g. "fr-CA", "fr", "en-CA") to the insights
 * language bucket. Anything that is not French falls back to the base English.
 */
export function normalizeLocaleToInsightsLang(locale?: string | null): InsightsLang {
  if (locale && locale.toLowerCase().startsWith('fr')) return 'fr';
  return BASE_INSIGHTS_LANG;
}

/** Translation locales required for a given country. CA needs French. */
export function translationLangsForCountry(country: CountryCode): InsightsLang[] {
  return country === CountryCodeList.CANADA ? ['fr'] : [];
}

/**
 * Returns the base `CityInsightsData` (without the `translations` map) for the
 * renderer, discarding the storage-only field.
 */
function toBaseData(stored: StoredCityInsights): CityInsightsData {
  const { translations: _translations, ...base } = stored;
  return base;
}

/**
 * Overlay the requested locale's text onto the shared base data.
 *
 * - Base locale: returns the stored payload as-is.
 * - Non-base locale with a translation: overlays the translated prose
 *   (`overview` + `aiOverview`) on top of the shared numeric data.
 * - Non-base locale *without* a translation yet: returns the base data with the
 *   AI prose stripped (`overview`/`aiOverview` omitted). The renderer then hides
 *   only the Overview card and data-sources popup — the locale-independent
 *   numeric cards (commute, amenities, groceries, …) still show, rather than
 *   the prose appearing in English on a translated page. Background generation
 *   backfills the translation (see `isLocaleTranslationMissing` / the trigger).
 */
export function selectLocalizedCityInsights(
  stored: StoredCityInsights,
  locale?: string | null
): CityInsightsData {
  const lang = normalizeLocaleToInsightsLang(locale);
  const base = toBaseData(stored);
  if (lang === BASE_INSIGHTS_LANG) return base;

  const translation = stored.translations?.[lang];
  if (!translation) {
    return { ...base, overview: undefined, aiOverview: undefined };
  }

  return {
    ...base,
    overview: translation.overview,
    aiOverview: translation.aiOverview,
  };
}

/**
 * Whether the given (already-stored) row is missing the translation for the
 * requested locale. Always false for the base language, which lives at the top
 * level of every row.
 */
export function isLocaleTranslationMissing(
  stored: StoredCityInsights,
  locale?: string | null
): boolean {
  const lang = normalizeLocaleToInsightsLang(locale);
  if (lang === BASE_INSIGHTS_LANG) return false;
  return !stored.translations?.[lang];
}
